import { ChangeEvent } from "react"
import { useFormikContext } from "formik"
import { BotMessageSquare, Blocks, CodeXml, User } from "lucide-react"
import { useIntegrationSettingsForm } from "../../../../hooks"
import { Integration } from "../../../../../types"
import { classNames } from "../../../../utils"

const OPTIONS = [
  {
    icon: Blocks,
    value: "standalone",
    label: "Standalone",
    description: "Component lives alongside other page content"
  },
  {
    icon: User,
    value: "boomtown",
    label: "LO Page",
    description: "Full-page loan officer profile with components"
  },
  {
    icon: CodeXml,
    value: "api",
    label: "API",
    description: "Launch from a custom button on your website"
  },
  {
    icon: BotMessageSquare,
    value: "chatbot",
    label: "Chatbot",
    description: "Conversational widget embedded on your site"
  }
]

interface Props {
  integration?: Integration
}

const EmbedsOptions = ({ integration }: Props) => {
  const { values, setFieldValue } = useFormikContext<Integration>()
  const { handleFormikChange } = useIntegrationSettingsForm(integration)

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFormikChange(e)
    setFieldValue("integration_type", e.target.value)
  }

  return (
    <fieldset>
      <legend className="sr-only">Embed type</legend>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {OPTIONS.map(option => {
          const active = values.type === option.value
          const Icon = option.icon
          const inputId = `embed-type-${option.value}`
          const descriptionId = `${inputId}-description`
          return (
            <label
              key={option.value}
              htmlFor={inputId}
              className={classNames(
                "relative flex cursor-pointer flex-col gap-2 rounded border-[1.5px] p-4 transition-all focus-within:ring-2 focus-within:ring-primary-300 focus-within:ring-offset-2",
                active
                  ? "border-primary-400 bg-primary-50"
                  : "border-neutral-200 bg-neutral-50 hover:border-neutral-300"
              )}
            >
              <input
                id={inputId}
                type="radio"
                name="type"
                value={option.value}
                checked={active}
                aria-describedby={descriptionId}
                onChange={onChange}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                <Icon
                  size={15}
                  aria-hidden="true"
                  className={active ? "text-primary-500" : "text-neutral-400"}
                />
                <span
                  className={classNames(
                    "text-sm font-medium",
                    active ? "text-neutral-900" : "text-neutral-600"
                  )}
                >
                  {option.label}
                </span>
              </div>
              <p
                id={descriptionId}
                className="text-xs leading-relaxed text-neutral-500 normal-case"
              >
                {option.description}
              </p>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export default EmbedsOptions
